import { AfterContentInit, Component, effect, ElementRef, HostListener, inject, input, OnDestroy, signal } from '@angular/core';

/** PrimeNG measures content without borders; include them before deciding to scroll. */
export function fitComposerInput(input: HTMLTextAreaElement): void {
  const style = getComputedStyle(input);
  const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
  const limit = parseFloat(style.maxHeight) || 160;
  const minimum = parseFloat(style.minHeight) || 40;
  input.style.height = 'auto';
  input.style.height = `${Math.min(Math.max(minimum, input.scrollHeight + borders), limit)}px`;
  input.style.overflowY = input.scrollHeight > input.clientHeight + 1 ? 'auto' : 'hidden';
}

@Component({
  selector: 'app-composer-controls',
  template: '<ng-content />',
  host: { '[class.unlocking]': 'hasBeenBusy() && !busy()', '[attr.aria-busy]': 'busy()' },
  styles: [`
    :host { display:flex;align-items:flex-end;gap:4px;min-width:0;flex-shrink:0; }
    :host ::ng-deep textarea {
      min-height:40px;max-height:min(160px,25vh);box-sizing:border-box;resize:none;
      line-height:22px;padding-block:8px;
      overflow-x:hidden;scrollbar-width:thin;scrollbar-color:#a6b8cd transparent;
    }
    :host ::ng-deep textarea::-webkit-scrollbar { width:6px; }
    :host ::ng-deep textarea::-webkit-scrollbar-thumb { background:#a6b8cd;border-radius:6px; }
    :host ::ng-deep textarea::-webkit-scrollbar-track { background:transparent; }
    :host ::ng-deep textarea::-webkit-scrollbar-button { display:none;width:0;height:0; }
    @supports selector(::-webkit-scrollbar) {
      :host ::ng-deep textarea { scrollbar-width:auto;scrollbar-color:auto; }
    }
    :host ::ng-deep textarea, :host ::ng-deep .p-button {
      transition:background-color 220ms ease,border-color 220ms ease,opacity 220ms ease,box-shadow 220ms ease;
    }
    :host(.unlocking) ::ng-deep textarea { animation:composer-ready 650ms ease-out 120ms; }
    @keyframes composer-ready {
      0% { box-shadow:0 0 0 0 #83a9d400; }
      35% { box-shadow:0 0 0 3px #83a9d433;border-color:#83a9d4; }
      100% { box-shadow:0 0 0 0 #83a9d400; }
    }
    @media(prefers-reduced-motion:reduce) {
      :host(.unlocking) ::ng-deep textarea { animation:none; }
      :host ::ng-deep textarea, :host ::ng-deep .p-button { transition:none; }
    }
  `],
})
export class ComposerControlsComponent implements AfterContentInit, OnDestroy {
  busy = input(false);
  responseActive = input(false);
  conversationKey = input('');
  autofocusOnOpen = input(false);
  protected hasBeenBusy = signal(false);
  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  private pendingFocus = false;
  private wasActive = false;
  private previousKey = '';
  private focusAtStart: Element | null = null;
  private frame?: number;
  private contentReady = false;
  constructor() {
    effect(() => {
      const busy = this.busy(), active = this.responseActive(), key = this.conversationKey();
      const autofocus = this.autofocusOnOpen();
      if (busy) this.hasBeenBusy.set(true);
      // Assigning the first chat ID belongs to the current send, not navigation.
      if (key !== this.previousKey && this.previousKey) {
        this.pendingFocus = autofocus;
        this.focusAtStart = document.activeElement;
      }
      this.previousKey = key;
      if (active && !this.wasActive) { this.pendingFocus = true; this.focusAtStart = document.activeElement; }
      this.wasActive = active;
      this.scheduleFocus();
    });
  }

  ngAfterContentInit(): void {
    this.contentReady = true;
    if (this.autofocusOnOpen()) {
      this.pendingFocus = true;
      this.focusAtStart = document.activeElement;
    }
    this.scheduleFocus();
  }

  private scheduleFocus(): void {
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    if (!this.contentReady || this.busy() || this.responseActive() || !this.pendingFocus) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = undefined;
      if (!this.pendingFocus || this.busy() || this.responseActive() || document.visibilityState === 'hidden') return;
      this.pendingFocus = false;
      const host = this.element.nativeElement;
      const current = document.activeElement;
      if (current !== document.body && current !== this.focusAtStart && current && !host.contains(current)) return;
      const input = host.querySelector('textarea');
      if (input && !input.disabled) input.focus({ preventScroll: true });
    });
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState !== 'hidden') this.scheduleFocus();
  }
  @HostListener('document:pointerdown', ['$event'])
  @HostListener('document:focusin', ['$event'])
  onUserFocus(event: Event) {
    if (event.target !== document.body && !this.element.nativeElement.contains(event.target as Node)) this.pendingFocus = false;
  }
  ngOnDestroy() { if (this.frame !== undefined) cancelAnimationFrame(this.frame); }
}
